const RELOAD_KEY = "relevoo.dynamic-import-reload";

const CHUNK_ERROR_PATTERNS = [
	"failed to fetch dynamically imported module",
	"error loading dynamically imported module",
	"unable to preload css",
	"importing a module script failed",
	"is not a valid javascript mime type",
];

export type ReloadGuard = {
	hasReloaded: () => boolean;
	markReloaded: () => void;
	clear: () => void;
	reload: () => void;
};

export const browserReloadGuard: ReloadGuard = {
	hasReloaded: () => {
		try {
			return window.sessionStorage.getItem(RELOAD_KEY) !== null;
		} catch {
			return true;
		}
	},
	markReloaded: () => {
		try {
			window.sessionStorage.setItem(RELOAD_KEY, "1");
		} catch {
			return;
		}
	},
	clear: () => {
		try {
			window.sessionStorage.removeItem(RELOAD_KEY);
		} catch {
			return;
		}
	},
	reload: () => {
		window.location.reload();
	},
};

export const isDynamicImportError = (error: unknown): boolean => {
	const message = error instanceof Error ? error.message : String(error);
	const normalized = message.toLowerCase();
	return CHUNK_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern));
};

export const importWithReload = async <T>(load: () => Promise<T>, guard: ReloadGuard): Promise<T> => {
	try {
		const loaded = await load();
		guard.clear();
		return loaded;
	} catch (error) {
		if (!isDynamicImportError(error)) {
			throw error;
		}
		try {
			const retried = await load();
			guard.clear();
			return retried;
		} catch (retryError) {
			if (!isDynamicImportError(retryError) || guard.hasReloaded()) {
				throw retryError;
			}
			guard.markReloaded();
			guard.reload();
			return await new Promise<T>(() => undefined);
		}
	}
};
